/**
 * Shared UI component for UI Datepicker.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';

import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Datepicker
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-datepicker',
  standalone: true,
  imports: [FormsModule, DatePickerModule, UiLabelTranslatePipe],
  templateUrl: './ui-datepicker.component.html',
  styleUrl: './ui-datepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiDatepickerComponent {
  /** PARENT-CHILD CONTRACT: label documents the input state boundary used by this file. */
  readonly label = input('');
  /** PARENT-CHILD CONTRACT: value documents the input state boundary used by this file. */
  readonly value = input('');
  /** PARENT-CHILD CONTRACT: placeholder documents the input state boundary used by this file. */
  readonly placeholder = input('Select date');
  /** PARENT-CHILD CONTRACT: id documents the input state boundary used by this file. */
  readonly id = input('');
  /** PARENT-CHILD CONTRACT: disabled documents the input state boundary used by this file. */
  readonly disabled = input(false);
  /** PARENT-CHILD CONTRACT: required documents the input state boundary used by this file. */
  readonly required = input(false);
  /** PARENT-CHILD CONTRACT: error documents the input state boundary used by this file. */
  readonly error = input('');
  /** PARENT-CHILD CONTRACT: hint documents the input state boundary used by this file. */
  readonly hint = input('');
  /** PARENT-CHILD CONTRACT: showIcon documents the input state boundary used by this file. */
  readonly showIcon = input(true);
  /** PARENT-CHILD CONTRACT: dateFormat documents the input state boundary used by this file. */
  readonly dateFormat = input('dd/mm/yy');
  readonly valueChange = output<string>();

  // computed so the Date reference is only recreated when value() actually changes,
  // preventing ngModel from seeing a new object on every change detection cycle.
  readonly dateValue = computed<Date | null>(() => {
    const raw = this.value();
    if (!raw) return null;
    // Strip time portion from ISO strings (e.g. "2026-04-29T00:00:00.000Z" → "2026-04-29")
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    // Parse as local date parts to avoid UTC-midnight shifting the date by one day
    // in timezones behind UTC (e.g. GMT+1 reads 2024-01-15T00:00Z as Jan 14 locally).
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  });

  /**
   * BUSINESS LOGIC: onDateChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDateChange(date: Date | null): void {
    if (!date) {
      this.valueChange.emit('');
      return;
    }
    // Serialize using local date parts so the emitted ISO string matches what the user picked.
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    this.valueChange.emit(`${y}-${m}-${d}`);
  }
}
